import { useRouter } from 'next/router'
import { useState } from 'react'
import { Space, Button, Divider } from 'antd'
import { useSelector } from 'react-redux'
import useTranslation from '@/hooks/useTranslation'
import { CAF_REQUEST_STATUS } from '../../../constants/CAFePatronato.constants'

import UploadMissingInfo from '../../../components/shared-components/modals/request/UploadMissingInfo'
import Files from '../../../components/shared-components/table/Files'
import DateAndTime from '../../../components/shared-components/table/DateAndTime'
import MissingInfo from '../../../components/shared-components/modals/request/MissingInfo'

import { AppTable, AppTableStatus } from '@/components/shared-components'
import FileTabLink from '../../../components/shared-components/FileTabLink'
import Sign from '../../../components/svgs/buttonIcons/Sign'
import Arrowright from '../../../components/svgs/buttonIcons/ArrowRight'
import UploadIcon from '../../../components/svgs/Upload'
import Refresh from '../../../components/svgs/auth/Refresh'
import Write from '../../../components/svgs/buttonIcons/Write'
import { ArrowDownOutlined } from '@ant-design/icons'

export default function TableC({ loading, requests }) {
  const { t } = useTranslation()
  const { push } = useRouter()

  const { profile, isCompleted } = useSelector((state) => state.auth)

  const [currentId, setCurrentId] = useState(null)
  const [missingOpen, setMissingOpen] = useState(false)
  const [uploadOpen, setUploadOpen] = useState(false)

  const [notes, setNotes] = useState(null)

  const columns = [
    {
      title: t('homenav.Services'),
      dataIndex: 'title',
      render: (text, record) => (
        <>
          <p className='block w-[10rem]'>{text}</p>
          <p
            className='text-green underline font-semibold cursor-pointer'
           onClick={() => push(`requests/${record.id}`)}
          >
            #{record.id_pratica}
          </p>
        </>
      ),
    },
    {
      title: t('Data richiesta / consegna '),
      dataIndex: 'request_at',
      render: (text, record) => (
        <div className='flex items-center gap-8'>
          {record.due_at && <ArrowDownOutlined className='text-primary' />}
          <div>
            <DateAndTime text={text} oneLine />
            {record.due_at && (
              <div className='mt-12'>
                <DateAndTime text={record.due_at} oneLine />
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      title: t('table.title.Status'),
      dataIndex: 'status',
      render: (text, record) => {
        const request_status = CAF_REQUEST_STATUS.find(
          (ele) => ele.value === text
        )
        return (
          <AppTableStatus
            text={t(request_status?.label)}
            color={request_status?.color}
            type={text === 2 ? 'LINK' : 'TAG'}
            onClick={() => {
              if (text === 2) {
                setNotes(record.required_missing_info)
                setMissingOpen(true)
              }
            }}
          />
        )
      },
    },
    {
      title: t('DSU ISEE'),
      dataIndex: 'transmission_files',
      render: (text) => <Files files={text} />,
    },
    {
      title: t('Ricevuta'),
      dataIndex: 'complete_files',
      render: (text, record) => {
        let signedDoc
        if (record.sign_at) signedDoc = record.signature_document
        return (
          <>
            {signedDoc ? <FileTabLink index={0} fileLink={signedDoc} /> : null}
            <Files files={text} hideDivider={signedDoc} increaseIndexBy={1} />
          </>
        )
      },
    },
    {
      title: t('table.title.Actions'),
      fixed: 'right',
      render: (_, record) => {
        const isWithoutFormService = Number(record.service?.without_form) === 1
        const isProfileIncomplete = !isCompleted
        const isDraftOrPendingStatus = record.status === 12 || record.status === 1
        const shouldShowCompleteModuleButton =
          isWithoutFormService &&
          isProfileIncomplete &&
          isDraftOrPendingStatus &&
          !record.reseller_id
        const shouldShowDraftFormButton =
          record.status === 12 &&
          record.new_form === 1 &&
          !record.reseller_id &&
          !isWithoutFormService
        const canShowEditButton =
          record.status !== 12 && record.status === 1 && !isWithoutFormService
        const hasNoRowActions = !(
          (record.status === 12 && record.new_form === 1) ||
          shouldShowCompleteModuleButton ||
          record.status === 1 ||
          record.status === 2 ||
          (record.status === 4 && record.service.is_isee === 1) ||
          (process.env.NEXT_PUBLIC_ENVIRONMET !== 'production' &&
            !record.sign_at &&
            record.status !== 12 &&
            record.service.document_type !== 0)
        )

        return (
        <>
          {hasNoRowActions ? (
            <Divider className='min-w-[6.8rem] border-[#DDD] my-0' />
          ) : (
            <div className='min-w-[6.8rem]'>
              <div>
                <Space size='middle'>
                  {shouldShowCompleteModuleButton && (
                    <Button
                      className='table-btn info !min-w-[10rem]'
                      onClick={() => {
                        push(`/dashboard/requests/${record.id}/edit`)
                      }}
                    >
                      <Arrowright />
                      {t('Completa modulo')}
                    </Button>
                  )}

                  {shouldShowDraftFormButton && (
                      <Button
                        className='table-btn info !min-w-[10rem]'
                        onClick={() => {
                          if (record.service.is_prepaid === 1) {
                            push(
                              `/dashboard/servizi/${record.service.slug}/pay`
                            )
                          } else {
                            push(
                              `/dashboard/servizi/${record.service.slug}/request`
                            )
                          }
                        }}
                      >
                        <Arrowright />
                        {t('Completa modulo')}
                      </Button>
                    )}

                  {record.status === 4 && record.service.is_isee === 1 && (
                    <Button
                      className='table-btn success !min-w-[10rem]'
                      onClick={() => {
                        if (record.service.is_prepaid === 1) {
                          push(
                            `/dashboard/servizi/${record.service.slug}/pay?action=renew`
                          )
                        } else {
                          push(
                            `/dashboard/servizi/${record.service.slug}/request?action=renew`
                          )
                        }
                      }}
                    >
                      <Refresh />
                      {t('Renew')}
                    </Button>
                  )}

                  {process.env.NEXT_PUBLIC_ENVIRONMET !== 'production' &&
                    !record.sign_at &&
                    record.status !== 12 &&
                    record.service.document_type !== 0 && (
                      <Button
                        className='table-btn primary !min-w-[10rem]'
                        onClick={() =>
                          push(
                            `/dashboard/servizi/${record.service.id}/request/sign?requestId=${record.id}`
                          )
                        }
                      >
                        <Sign />
                        {t('Firma Mandato')}
                      </Button>
                    )}

                  {record.status === 2 && (
                    <Button
                      className='table-btn success !min-w-[10rem]'
                      onClick={() => {
                        setCurrentId(record.id)
                        setNotes(record.required_missing_info)
                        setUploadOpen(true)
                      }}
                    >
                      <UploadIcon />
                      {t('button.UploadInfo')}
                    </Button>
                  )}

                  {canShowEditButton && (
                      <Button
                        className='table-btn primary !min-w-[10rem]'
                        onClick={() => {
                          push(`/dashboard/requests/${record.id}/edit`)
                        }}
                      >
                        <Write color='#006fad' />
                        {t('Edit')}
                      </Button>
                    )}
                </Space>
              </div>
            </div>
          )}
        </>
        )
      },
    },
  ]

  const canaleColumn =
    profile?.role === 'offline_client'
      ? {
        title: 'Canale',
        dataIndex: 'reseller_id',
        render: (text) => (
          <span className='font-semibold'>{text ? 'Partner' : 'Online'}</span>
        ),
      }
      : null

  if (canaleColumn) columns.splice(1, 0, canaleColumn)

  return (
    <>
      <AppTable loading={loading} columns={columns} dataSource={requests} />
      <UploadMissingInfo
        open={uploadOpen}
        setOpen={setUploadOpen}
        id={currentId}
        notes={notes}
      />
      <MissingInfo open={missingOpen} setOpen={setMissingOpen} notes={notes} />
    </>
  )
}
